import { Option, Pagination, Resource } from "@/helpers/Interfaces";
import Pager from "@/helpers/Pager";
import axios from "axios";
import { merge } from "lodash";
import { DateTime } from "luxon";
import { ChatRoomsListItemModel } from "@/modules/renter/chat/services/ChatService";
import { $t } from "@/plugins/localization";
import { TaskLinkModel } from "@/modules/renter/tasks/services/TasksService";

/**
 * ReservationsService
 */
export default class ReservationsService
{
    /**
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/reservations', {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/reservations/${publicId}`)).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getReservationsAutocompleteList(search: string, limit: number = 1, propertyPublicId: string = null): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/renters/reservations/autocomplete', {
            params: {
                search: search,
                limit: limit,
                propertyPublicId
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<CityItemModel>
     */
    public static async fetchReservationAutocompleteModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/reservations/autocomplete/${publicId}`)).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getReservationsWebSourceAutocompleteList(search: string, limit: number = 10): Promise<string[]>
    {
        return (await axios.get<string[]>('admin/renters/webSource/autocomplete', {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<CityItemModel>
     */
    public static async fetchReservationWebSourceAutocompleteModel(search: string): Promise<string>
    {
        return (await axios.get<string>(`admin/renters/webSource/autocomplete/single`, {
            params: { search }
        })).data;
    }

    /**
     *
     * @returns Promise<string>
     */
    public static async runReservationsSyncJob(form: GuestyReservationSyncFormModel): Promise<string>
    {
        return (await axios.post<string>(`admin/renters/reservations/synchronization`, form)).data;
    }
}

export interface ListItemModel
{
    id: number;
    publicId: string;
    propertyPublicId: string;
    propertyName: string;
    clientName: string;
    clientSurname: string;
    reservationType: string;
    clientTelephone: string;
    clientEmail: string;
    numberOfGuests?: number;
    numberOfNights: number;
    avantioBookingCode: number;
    avantioLocalizator: string;
    isPet: boolean;
    startDate: DateTime;
    endDate: DateTime;
    arrivalDate: DateTime;
    departureDate: DateTime;
    bookingType: string;
    borderColor: string;
    city: string;
    webSource: string;
}

export interface FilterModel
{
    today: boolean;
    tomorrow: boolean;
    arrival: boolean;
    departure: boolean;
    custom?: DateTime;
    localizator: string;
    bookingTypes: Array<string> | string;
    citiesIds: string[] | string;
    districtsIds: string[] | string;
    propertiesIds: any[] | string;
    webSource: string;
}

export interface FormModel
{
    id: number;
    publicId: string;
    propertyPublicId: string;
    guestyId: string
    bookingCode: number;
    localizator: string;
    bookingType: string;
    borderColor: string;
    accommodationCode?: number;
    startDate?: DateTime;
    endDate?: DateTime;
    arrivalDate?: DateTime;
    departureDate?: DateTime;
    clientName: string;
    clientSurname: string;
    clientTelephone: string;
    clientEmail: string;
    numberOfGuests?: number;
    numberOfNights: number;
    adultsNumber?: number;
    child1Age?: number;
    child2Age?: number;
    child3Age?: number;
    child4Age?: number;
    child5Age?: number;
    child6Age?: number;
    isPet: boolean;
    reservationChats: ChatRoomsListItemModel[];
    reservationTasks: TaskLinkModel[];
    webSource: string;
    price: number;
}

export interface ReservationsSyncJobFormModel {
    dateReservationFromUtc: DateTime;
    dateReservationToUtc: DateTime;
    dateModificationFromUtc: DateTime;
    dateModificationToUtc: DateTime;
    returnCancelled: boolean;
}

export interface GuestyReservationSyncFormModel {
    checkoutDateFromUtc: DateTime;
    checkoutDateToUtc: DateTime;
    dateModifiedFromUtc: DateTime;
    dateModifiedToUtc: DateTime;
}

enum StatusEnum {
    PROPIETARIO = 'PROPIETARIO',
    UNAVAILABLE = 'UNAVAILABLE',
    inquiry = 'inquiry',
    declined = 'declined',
    expired = 'expired',
    canceled = 'canceled',
    closed = 'closed',
    reserved = 'reserved',
    confirmed = 'confirmed'
}

const StatusOptions = (): Option[] => ([
    { value: StatusEnum.PROPIETARIO, text: $t('[[[Rezerwacja właściciela]]]') },
    { value: StatusEnum.UNAVAILABLE, text: $t('[[[Niedostępne]]]') },
    { value: StatusEnum.inquiry, text: $t('[[[Zapytanie]]]') },
    { value: StatusEnum.declined, text: $t('[[[Odrzucone]]]') },
    { value: StatusEnum.expired, text: $t('[[[Wygasłe]]]') },
    { value: StatusEnum.canceled, text: $t('[[[Anulowane]]]') },
    { value: StatusEnum.closed, text: $t('[[[Zamknięte]]]') },
    { value: StatusEnum.reserved, text: $t('[[[Zarezerwowane]]]') },
    { value: StatusEnum.confirmed, text: $t('[[[Potwierdzone]]]') }
]);

const StatusName = (value: string | StatusEnum): string =>
{
    const statusOption = StatusOptions().find(s => s.value === value);

    return statusOption?.text || value;
};

export {
    StatusEnum,
    StatusOptions,
    StatusName
};

export interface ReservationItemModel extends FormModel {}
